/**
 * Puente de migración auth — Structura Food Market.
 * Fase dual: JWT Bearer (localStorage) → cookies HttpOnly.
 *
 * Port de Booking Hub session-bridge.ts con naming sm_*.
 * Controlar migración con SESSION_TRANSPORT env var:
 *   localStorage → solo Bearer (legacy)
 *   dual         → Bearer primero, luego cookie (default durante migración)
 *   cookie       → solo cookie (fase final)
 */

export const SM_SESSION_COOKIE_NAME = "sm_session";
export const SM_CSRF_COOKIE_NAME = "sm_csrf";

export const SM_SESSION_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 30; // 30 días

export type SessionTransport = "localStorage" | "cookie" | "dual";

/** Default: dual — mantiene retrocompatibilidad con clientes Bearer durante migración. */
export const CURRENT_SM_SESSION_TRANSPORT: SessionTransport = "dual";

function readTransportEnv(): string | undefined {
  if (typeof process !== "undefined" && process.env.SESSION_TRANSPORT) {
    return process.env.SESSION_TRANSPORT;
  }
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_SESSION_TRANSPORT) {
    return String(import.meta.env.VITE_SESSION_TRANSPORT);
  }
  return undefined;
}

export function getSmSessionTransport(): SessionTransport {
  const raw = readTransportEnv()?.toLowerCase();
  if (raw === "cookie" || raw === "dual" || raw === "localstorage") {
    return raw === "localstorage" ? "localStorage" : (raw as SessionTransport);
  }
  return CURRENT_SM_SESSION_TRANSPORT;
}

export function isSmCookiePrimaryTransport(): boolean {
  return getSmSessionTransport() === "cookie";
}

export function shouldSmPersistBearerInClient(): boolean {
  const t = getSmSessionTransport();
  return t === "dual" || t === "localStorage";
}

function isProduction(): boolean {
  return typeof process !== "undefined"
    ? process.env.NODE_ENV === "production"
    : !import.meta.env?.DEV;
}

export function smSessionCookieAttributes(): string {
  const parts = ["Path=/", "HttpOnly", "SameSite=Lax"];
  if (isProduction()) parts.push("Secure");
  return parts.join("; ");
}

export function smCsrfCookieAttributes(): string {
  const parts = ["Path=/", "SameSite=Lax"];
  if (isProduction()) parts.push("Secure");
  return parts.join("; ");
}

function parseCookieHeader(header: string | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!header) return out;
  for (const part of header.split(";")) {
    const [rawKey, ...rest] = part.trim().split("=");
    if (!rawKey) continue;
    out[rawKey.trim()] = decodeURIComponent(rest.join("=") || "");
  }
  return out;
}

export function readSmBearerFromRequest(request: Request): string | null {
  const h = request.headers.get("Authorization");
  if (!h?.startsWith("Bearer ")) return null;
  return h.slice(7).trim() || null;
}

/** Chequeo liviano sin jsonwebtoken (safe en cliente y servidor). Solo para elegir Bearer vs cookie en dual. */
function isSessionJwtUsable(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(
      typeof atob !== "undefined" ? atob(b64) : Buffer.from(b64, "base64").toString("utf8"),
    ) as { id?: string; exp?: number };
    if (payload.exp != null && payload.exp * 1000 < Date.now()) return false;
    return Boolean(payload.id);
  } catch {
    return false;
  }
}

/**
 * Resuelve el JWT de sesión según SESSION_TRANSPORT.
 * - cookie: la cookie HttpOnly manda (evita Bearer viejo en localStorage de otro usuario).
 * - dual: Bearer válido primero; si expiró o es inválido, cae a cookie.
 * - localStorage: solo Bearer.
 */
export function resolveSmSessionToken(request: Request): string | null {
  const transport = getSmSessionTransport();
  const bearer = readSmBearerFromRequest(request);
  const cookies = parseCookieHeader(request.headers.get("cookie"));
  const cookieToken = cookies[SM_SESSION_COOKIE_NAME]?.trim() || null;

  if (transport === "cookie") return cookieToken || bearer;
  if (transport === "localStorage") return bearer;

  // dual
  if (bearer && isSessionJwtUsable(bearer)) return bearer;
  if (cookieToken) return cookieToken;
  return bearer;
}

export function hasSmSessionCookie(request: Request): boolean {
  return parseCookieHeader(request.headers.get("cookie"))[SM_SESSION_COOKIE_NAME] !== undefined;
}

/**
 * Genera los headers Set-Cookie para sesión + CSRF al hacer login.
 * Retorna [] si el transport es solo localStorage.
 */
export function createSmSessionCookieHeaders(token: string): string[] {
  if (getSmSessionTransport() === "localStorage") return [];
  // crypto.randomUUID disponible en Node 18+ y browser
  const cryptoObj = typeof crypto !== "undefined" ? crypto : require("node:crypto").webcrypto;
  const csrf = cryptoObj.randomUUID();
  return [
    `${SM_SESSION_COOKIE_NAME}=${encodeURIComponent(token)}; ${smSessionCookieAttributes()}; Max-Age=${SM_SESSION_COOKIE_MAX_AGE_SEC}`,
    `${SM_CSRF_COOKIE_NAME}=${encodeURIComponent(csrf)}; ${smCsrfCookieAttributes()}; Max-Age=${SM_SESSION_COOKIE_MAX_AGE_SEC}`,
  ];
}

/** Headers para limpiar cookies en logout. */
export function clearSmSessionCookieHeaders(): string[] {
  return [
    `${SM_SESSION_COOKIE_NAME}=; Path=/; Max-Age=0`,
    `${SM_CSRF_COOKIE_NAME}=; Path=/; Max-Age=0`,
  ];
}

/**
 * Double-submit CSRF para requests mutating cuando la sesión viaja en cookie.
 * Clientes solo-Bearer siguen funcionando (CSRF no aplica a Bearer).
 */
export function validateSmCsrfForMutation(request: Request): boolean {
  const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
  if (!MUTATION_METHODS.has(request.method)) return true;

  // Bearer token => auth no depende de cookie, sin vector CSRF.
  const auth = request.headers.get("Authorization") ?? request.headers.get("authorization");
  if (auth?.startsWith("Bearer ") && auth.slice(7).trim()) return true;

  if (!hasSmSessionCookie(request)) return true; // Bearer-only: no CSRF

  const cookies = parseCookieHeader(request.headers.get("cookie"));
  const cookieCsrf = cookies[SM_CSRF_COOKIE_NAME];
  const headerCsrf =
    request.headers.get("X-CSRF-Token") ?? request.headers.get("x-csrf-token");
  return Boolean(cookieCsrf && headerCsrf && cookieCsrf === headerCsrf);
}
